/**
 * usePermissions Hook
 *
 * Provides permission checking based on user group membership.
 * Centralises permission logic for the home/map feature.
 */
import { useMemo } from 'react';
import { useHomeBootstrap } from './useHomeBootstrap';
/**
 * Hook for checking user permissions based on group membership.
 *
 * Groups that can modify BFT and Conduit records:
 * - admins
 * - planners
 *
 * @returns Permissions object with boolean flags and helper functions
 */
export const usePermissions = () => {
    const { groups } = useHomeBootstrap();
    return useMemo(() => {
        // Normalise group names to lowercase for comparison
        const normalisedGroups = groups.map(g => g.toLowerCase());
        // Check if user is member of any of the specified groups
        const isMemberOf = (groupNames) => {
            const toCheck = Array.isArray(groupNames) ? groupNames : groupNames.split(',');
            return toCheck.some(name => normalisedGroups.includes(name.trim().toLowerCase()));
        };
        // Check for admin status
        const isAdmin = isMemberOf(['admins', 'admin']);
        // Permission to modify BFT records (admins or planners)
        const canModifyBft = isMemberOf(['admins', 'admin', 'planners']);
        // Permission to modify conduits (admins or planners)
        const canModifyConduit = isMemberOf(['admins', 'admin', 'planners']);
        return {
            groups,
            isAdmin,
            canModifyBft,
            canModifyConduit,
            isMemberOf,
        };
    }, [groups]);
};
export default usePermissions;
